import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Space, Row, Col, Card, Divider, Select, Switch, Collapse, DatePicker, Modal } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';

const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;

const ClientForm = ({ initialValues, onSubmit, onCancel }) => {
  const [form] = Form.useForm();
  const [activeKey, setActiveKey] = useState(['1']);

  useEffect(() => {
    if (initialValues) {
      // Adaptăm datele pentru a le face compatibile cu formularul
      const adaptedValues = {
        ...initialValues,
        rechizite_bancare: initialValues.rechizite_bancare || [],
        adresa_fizica: initialValues.adresa_fizica || [],
        contact: initialValues.contact || [],
        manager_atasat: initialValues.manager_atasat || [],
        comentariu: initialValues.comentariu || []
      };
      form.setFieldsValue(adaptedValues);
    }
  }, [initialValues, form]);

  const handleSubmit = async (values) => {
    await onSubmit(values);
  };

  // Componente pentru secțiunile dinamice
  const RechiziteBancareSection = () => (
    <Form.List name="rechizite_bancare">
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name, ...restField }) => (
            <Card 
              key={key} 
              size="small" 
              className="mb-4"
              title={`Cont bancar ${name + 1}`}
              extra={
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => remove(name)}
                />
              }
            >
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    {...restField}
                    name={[name, 'iban']}
                    label="IBAN"
                    rules={[{ pattern: /^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/, message: 'Format IBAN invalid' }]}
                  >
                    <Input placeholder="RO49AAAA1B31007593840000" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    {...restField}
                    name={[name, 'bic']}
                    label="BIC/SWIFT"
                  >
                    <Input placeholder="AAAAROBB" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    {...restField}
                    name={[name, 'banca']}
                    label="Bancă"
                  >
                    <Input placeholder="Numele băncii" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          ))}
          <Button
            type="dashed"
            onClick={() => add()}
            block
            icon={<PlusOutlined />}
            className="mb-4"
          >
            Adaugă cont bancar
          </Button>
        </>
      )}
    </Form.List>
  );

  const AdresaFizicaSection = () => (
    <Form.List name="adresa_fizica">
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name, ...restField }) => (
            <Card
              key={key}
              size="small"
              className="mb-4"
              title={`Adresă fizică ${name + 1}`}
              extra={
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => remove(name)}
                />
              }
            >
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    {...restField}
                    name={[name, 'tip']}
                    label="Tip adresă"
                  >
                    <Select placeholder="Selectează tipul">
                      <Option value="sediu">Sediu principal</Option>
                      <Option value="depozit">Depozit</Option>
                      <Option value="punct_lucru">Punct de lucru</Option>
                      <Option value="punct_vanzare">Punct de vânzare</Option>
                      <Option value="alta">Altă adresă</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    {...restField}
                    name={[name, 'oras']}
                    label="Oraș"
                  >
                    <Input placeholder="Orașul" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    {...restField}
                    name={[name, 'comentariu']}
                    label="Comentariu"
                  >
                    <Input placeholder="Detalii suplimentare" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                {...restField}
                name={[name, 'adresa']}
                label="Adresa completă"
              >
                <TextArea rows={2} placeholder="Strada, numărul, etc." />
              </Form.Item>
            </Card>
          ))}
          <Button
            type="dashed"
            onClick={() => add()}
            block
            icon={<PlusOutlined />}
            className="mb-4"
          >
            Adaugă adresă fizică
          </Button>
        </>
      )}
    </Form.List>
  );

  const ContactSection = () => (
    <Form.List name="contact">
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name, ...restField }) => (
            <Card
              key={key}
              size="small"
              className="mb-4"
              title={`Contact ${name + 1}`}
              extra={
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => remove(name)}
                />
              }
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    {...restField}
                    name={[name, 'nume_prenume']}
                    label="Nume și prenume"
                  >
                    <Input placeholder="Numele persoanei de contact" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    {...restField}
                    name={[name, 'pozitie']}
                    label="Poziția"
                  >
                    <Input placeholder="Manager, Director, etc." />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    {...restField}
                    name={[name, 'telefon']}
                    label="Telefon"
                    rules={[{ pattern: /^[0-9+\s\-()]+$/, message: 'Format telefon invalid' }]}
                  >
                    <Input placeholder="+40 XXX XXX XXX" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    {...restField}
                    name={[name, 'email']}
                    label="Email"
                    rules={[{ type: 'email', message: 'Format email invalid' }]}
                  >
                    <Input placeholder="email@domeniu.com" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    {...restField}
                    name={[name, 'principal_dahnu']}
                    label="Contact principal"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          ))}
          <Button
            type="dashed"
            onClick={() => add()}
            block
            icon={<PlusOutlined />}
            className="mb-4"
          >
            Adaugă persoană de contact
          </Button>
        </>
      )}
    </Form.List>
  );

  const ManagerAtasatSection = () => (
    <Form.List name="manager_atasat">
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name, ...restField }) => (
            <Card
              key={key}
              size="small"
              className="mb-4"
              title={`Manager ataşat ${name + 1}`}
              extra={
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => remove(name)}
                />
              }
            >
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    {...restField}
                    name={[name, 'nume_prenume']}
                    label="Nume și prenume"
                  >
                    <Input placeholder="Numele managerului" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    {...restField}
                    name={[name, 'pozitie']}
                    label="Poziția"
                  >
                    <Input placeholder="Account Manager, etc." />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    {...restField}
                    name={[name, 'telefon']}
                    label="Telefon"
                    rules={[{ pattern: /^[0-9+\s\-()]+$/, message: 'Format telefon invalid' }]}
                  >
                    <Input placeholder="+40 XXX XXX XXX" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          ))}
          <Button
            type="dashed"
            onClick={() => add()}
            block
            icon={<PlusOutlined />}
            className="mb-4"
          >
            Adaugă manager ataşat
          </Button>
        </>
      )}
    </Form.List>
  );

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      className="max-h-[70vh] overflow-y-auto"
      scrollToFirstError
    >
      <Collapse
        activeKey={activeKey}
        onChange={setActiveKey}
        className="mb-6"
      >
        <Panel header="Informații de bază" key="1" className="font-semibold">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="denumire"
                label="Denumire"
                rules={[{ required: true, message: 'Denumirea este obligatorie' }]}
              >
                <Input placeholder="Denumirea companiei" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="id_no"
                label="CF"
                rules={[{ pattern: /^(RO)?[0-9]+$/, message: 'Format CF invalid' }]}
              >
                <Input placeholder="RO12345678 sau 12345678" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="cod_tva"
                label="Cod TVA"
                rules={[{ pattern: /^(RO)?[0-9]+$/, message: 'Format cod TVA invalid' }]}
              >
                <Input placeholder="RO12345678" />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                name="website"
                label="Website"
                rules={[{ type: 'url', message: 'Format URL invalid' }]}
              >
                <Input placeholder="https://example.com" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="adresa_juridica"
            label="Adresa juridică"
          >
            <TextArea 
              rows={3} 
              placeholder="Adresa completă a sediului social"
            />
          </Form.Item>
        </Panel>

        <Panel header="Termeni comerciali" key="2" className="font-semibold">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="termen_de_plata"
                label="Termen de plată"
              >
                <Select placeholder="Selectează termenul">
                  <Option value="imediat">Imediat</Option>
                  <Option value="15 zile">15 zile</Option>
                  <Option value="30 zile">30 zile</Option>
                  <Option value="45 zile">45 zile</Option>
                  <Option value="60 zile">60 zile</Option>
                  <Option value="90 zile">90 zile</Option>
                  <Option value="altul">Altul (specificați în comentarii)</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="termen_de_livrare"
                label="Termen de livrare"
              >
                <Input placeholder="ex: 5-7 zile lucrătoare" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="tip_de_achitare"
                label="Tip de achitare"
              >
                <Select placeholder="Selectează tipul">
                  <Option value="transfer_bancar">Transfer bancar</Option>
                  <Option value="numerar">Numerar</Option>
                  <Option value="card">Card</Option>
                  <Option value="cecuri">Cecuri</Option>
                  <Option value="mixt">Mixt</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Panel>

        <Panel header="Conturi bancare" key="3" className="font-semibold">
          <RechiziteBancareSection />
        </Panel>

        <Panel header="Adrese fizice" key="4" className="font-semibold">
          <AdresaFizicaSection />
        </Panel>

        <Panel header="Persoane de contact" key="5" className="font-semibold">
          <ContactSection />
        </Panel>

        <Panel header="Manageri ataşați" key="6" className="font-semibold">
          <ManagerAtasatSection />
        </Panel>
      </Collapse>

      <div className="flex justify-end space-x-2 pt-4 border-t bg-white sticky bottom-0">
        <Button onClick={onCancel} size="large">
          Anulează
        </Button>
        <Button type="primary" htmlType="submit" size="large">
          {initialValues ? 'Actualizează' : 'Creează'} Client
        </Button>
      </div>
    </Form>
  );
};

export default ClientForm; 